import PageLayout from '@app-studio-web/ui/components/PageLayout'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface Props {
  onPrepare?: () => Promise<{ success: boolean }>
}

function launchConfetti() {
  const container = document.createElement('div')
  container.style.cssText =
    'position:fixed;inset:0;pointer-events:none;z-index:200;overflow:hidden'
  document.body.appendChild(container)

  const colors = [
    '#4A8FD9',
    '#F0C24E',
    '#E8734A',
    '#4BBF8A',
    '#D96BA0',
    '#7AB4E8',
    '#F5A623',
  ]
  for (let i = 0; i < 80; i++) {
    const p = document.createElement('div')
    const dur = (Math.random() * 2 + 1.5).toFixed(2)
    const delay = (Math.random() * 0.8).toFixed(2)
    const size = Math.random() * 8 + 5
    p.style.cssText = `
      position:absolute;top:-10px;
      left:${Math.random() * 100}%;
      width:${size}px;height:${size}px;
      background:${colors[Math.floor(Math.random() * colors.length)]};
      border-radius:${Math.random() > 0.5 ? '50%' : '2px'};
      animation:confettiFall ${dur}s ease-in ${delay}s forwards;
    `
    container.appendChild(p)
  }
  setTimeout(() => container.remove(), 4000)
}

export default function MatchPreparingPage({ onPrepare }: Props = {}) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [percent, setPercent] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const targetRef = useRef(94 + Math.floor(Math.random() * 5))
  const prepareStarted = useRef(false)

  // Run onPrepare (saveOnboardingAnswers) in the background
  useEffect(() => {
    if (!onPrepare || prepareStarted.current) return
    prepareStarted.current = true
    onPrepare()
  }, [onPrepare])

  useEffect(() => {
    const target = targetRef.current
    let current = 0
    const fastThreshold = target - 10
    let cancelled = false
    let slowTimer: ReturnType<typeof setTimeout>
    let navTimer: ReturnType<typeof setTimeout>

    const finalize = () => {
      setIsComplete(true)
      launchConfetti()
      navTimer = setTimeout(() => {
        if (!cancelled) navigate('/checkout/plans')
      }, 2500)
    }

    const fastTimer = setInterval(() => {
      current++
      if (current >= fastThreshold) {
        clearInterval(fastTimer)
        const slowCount = (c: number) => {
          if (cancelled) return
          if (c > target) {
            finalize()
            return
          }
          setPercent(c)
          slowTimer = setTimeout(() => slowCount(c + 1), 120)
        }
        slowCount(current)
        return
      }
      setPercent(current)
    }, 25)

    return () => {
      cancelled = true
      clearInterval(fastTimer)
      clearTimeout(slowTimer)
      clearTimeout(navTimer)
    }
  }, [navigate])

  return (
    <PageLayout>
      <div className="flex grow flex-col items-center justify-center gap-6 text-center">
        {/* Match circle */}
        <div className="relative mb-6 h-[200px] w-[200px] rounded-full">
          <div
            className={`absolute inset-0 rounded-full border-4 transition-[border-color,box-shadow] duration-500 ${
              isComplete
                ? 'border-[#2D2D2D] shadow-[0_0_30px_rgba(45,45,45,0.25)]'
                : 'border-[#E4DFD6]'
            }`}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-primary font-serif text-[3.2rem] font-normal tabular-nums">
              {percent}%
            </span>
          </div>
        </div>

        {/* Compatibility Score label */}
        <p className="text-[0.75rem] font-semibold tracking-[2px] text-[#A09A90] uppercase">
          Compatibility Score
        </p>

        {/* Title */}
        <h2 className="text-primary font-serif text-3xl font-normal">
          {isComplete ? t('pages.onboarding.match.title') : 'Calculating...'}
        </h2>

        {/* Typing dots */}
        {!isComplete && (
          <div className="my-5 flex justify-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-full bg-[#4A4A4A]"
              style={{ animation: 'typingBounce 1.4s ease-in-out infinite' }}
            />
            <span
              className="h-2.5 w-2.5 rounded-full bg-[#4A4A4A]"
              style={{
                animation: 'typingBounce 1.4s ease-in-out 0.2s infinite',
              }}
            />
            <span
              className="h-2.5 w-2.5 rounded-full bg-[#4A4A4A]"
              style={{
                animation: 'typingBounce 1.4s ease-in-out 0.4s infinite',
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-12px); opacity: 1; }
        }
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </PageLayout>
  )
}
